import { ResponseStatus } from '@shared/models/base/response-status.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject } from '@angular/core';
import { PurchaseService } from '@features/purchase/services/purchase.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import {
  BasicPaymentInfo,
  Order,
  ORDER_INITIAL_STATE,
  OrderPayment,
  PAYMENT_INFO_INITIAL_STATE,
  PurchaseMode,
  PurchaseResponseCodes
} from '@features/purchase/models/order.model';
import {
  BUNDLED_PRODUCT_INITIAL_STATE,
  BundledProduct,
  BundledState,
  NMFCBookDetail,
  Product,
  PRODUCT_INITIAL_STATE,
  ProductType
} from '@features/purchase/models/product.model';
import { produce } from 'immer';
import { CompanyFormData, CompanyType, CUSTOMER_TYPE_OPTIONS } from '@features/purchase/models/company.model';
import { UserStore } from '@shared/stores/user.store';
import { PaymentPayload } from '@shared/components/organisms/payment/model/payment-payload.model';
import { IResponse } from '@shared/models/base/http-response.model';
import { GLAccount } from '@shared/components/organisms/payment/model/gl-account.model';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { Logger } from '@shared/services/logger';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RequestMembershipInfo } from '@features/purchase/models/request-membership-info.model';
import { CompanyStore } from '@features/profile/store/company.store';
import { SubscriptionStore } from '@features/profile/store/subscription.store';
import { Company } from '@features/profile/models/company.model';
import { MemberIdResponseCodes } from '@features/purchase/models/enums/member-id-response-codes.enum';
import { AvailableCompanyUser } from '@features/purchase/models/search-available-companies-for-purchase.model';
import { CommonStore } from '@shared/stores/common.store';

export enum PurchaseInitializationState {
  LOADING_COMPANY,
  LOADING_SUBSCRIPTIONS,
  LOADING_PRODUCTS,
  INITIALIZED
}

interface Purchase {
  products: Product[];
  bfdProducts: Product[];
  bundledState: BundledState;
  purchaseInitializationState: PurchaseInitializationState;
  productResponseStatus: ResponseStatus;
  membershipVerification: boolean;
  membershipResponseStatus: ResponseStatus;
  selectedCompany: Company | null;
  selectedProduct: Product;
  basicPaymentInfo: BasicPaymentInfo;
  pricePerLicense: number;
  additionalLicensesPurchased: boolean;
  order: Order;
  orderNumber: string;
  orderSubmissionStatus: ResponseStatus;
  completedSteps: boolean[];
  websiteSeatsNationallyCapped: boolean;
  suggestedCompanyName: string;
  paymentInfoEntered: boolean;
  adobeSigningUrl: string | null;
  nmfcBookDetail: NMFCBookDetail;
  isLicenseThenBook: boolean;
}

export type PurchaseState = Purchase;

export const initialState: PurchaseState = {
  products: [],
  bfdProducts: [],
  bundledState: BUNDLED_PRODUCT_INITIAL_STATE,
  membershipVerification: false,
  purchaseInitializationState: PurchaseInitializationState.LOADING_COMPANY,
  productResponseStatus: ResponseStatus.IDLE,
  membershipResponseStatus: ResponseStatus.IDLE,
  selectedCompany: null,
  selectedProduct: PRODUCT_INITIAL_STATE,
  basicPaymentInfo: PAYMENT_INFO_INITIAL_STATE,
  pricePerLicense: 0,
  additionalLicensesPurchased: false,
  order: ORDER_INITIAL_STATE,
  orderNumber: '',
  orderSubmissionStatus: ResponseStatus.IDLE,
  completedSteps: [false, false, false, false, false, false],
  websiteSeatsNationallyCapped: false,
  suggestedCompanyName: '',
  paymentInfoEntered: false,
  adobeSigningUrl: null,
  nmfcBookDetail: {
    name: '',
    effectiveDate: '',
    expirationDate: '',
    issuedDate: '',
    issuedBy: '',
    issuedByAddress: '',
    issuedByWebsite: ''
  },
  isLicenseThenBook: false
};

const PRODUCTS_STORAGE_KEY = 'products';
const BFD_PRODUCTS_STORAGE_KEY = 'bfd-products';

export const PurchaseStore = signalStore(
  { providedIn: 'root' },
  withDevtools('purchase'),
  withState(() => {
    const sessionStorageService = inject(SessionStorageService);
    const sessionProducts = sessionStorageService.getItem<Product[]>(PRODUCTS_STORAGE_KEY) || [];
    const bfdSessionProducts = sessionStorageService.getItem<Product[]>(BFD_PRODUCTS_STORAGE_KEY) || [];

    return {
      ...initialState,
      products: sessionProducts.length ? sessionProducts : [],
      bfdProducts: bfdSessionProducts.length ? bfdSessionProducts : [],
      productResponseStatus: sessionProducts.length && bfdSessionProducts.length ? ResponseStatus.LOADED : ResponseStatus.IDLE,
    };
  }),
  withComputed((store) => ({
    webProducts: computed(() => {
      return store
        .products()
        .filter((product) => product.sku === (store.bundledState().isBundled ? 'WEBAPPB' : 'WEBAPP'))
        .sort((a, b) => b.memberCost - a.memberCost);
    }),
    allWebProducts: computed(() => {
      return store
        .products()
        .filter((product) => ['WEBAPPB', 'WEBAPP'].includes(product.sku));
    }),
    apiProducts: computed(() => {
      const companyType = store.order().companyInformation.companyType;
      const mappedCustomerType = CUSTOMER_TYPE_OPTIONS[companyType];

      return store
        .products()
        .filter(
          (product) =>
            product.sku === (store.bundledState().isBundled ? 'CSTAPIB' : 'CSTAPI') &&
            product.CustomerType?.trim().toLowerCase() === mappedCustomerType?.trim().toLowerCase(),
        )
        .sort((a, b) => b.memberCost - a.memberCost);
    }),
    filteredBfdProducts: computed(() => {
      const companyType = store.order().companyInformation.companyType;
      const mappedCustomerType = CUSTOMER_TYPE_OPTIONS[companyType];

      return store
        .bfdProducts()
        .filter((product) => product.CustomerType?.trim().toLowerCase() === mappedCustomerType?.trim().toLowerCase())
        .sort((a, b) => b.memberCost - a.memberCost);
    }),
    nmfcBookProduct: computed(() => {
      return store.products().filter((product) => product.sku === ('NMFCBK'))
    }),
    glAccounts: computed(() => {
      // get unique gl accounts from products and return as GLAccount
      const products = store.products()
        .concat(store.bfdProducts());

      const uniqueGLAccounts = Array.from(new Set(products.map((product) => product.glAccount)))
        .sort();

      return uniqueGLAccounts.map((glCode, index) => {
        const glAccount: GLAccount = {
          value: index,
          title: glCode,
        };

        return glAccount;
      });
    }),
    companyFullAddress: computed(() => {
      const companyInfo = store.order().companyInformation;
      return `${companyInfo.address1}, ${companyInfo.city}, ${companyInfo.region} ${companyInfo.postalCode}, ${companyInfo.country}`;
    }),
    shippingFullAddress: computed(() => {
      const shipInfo = store.order().shippingInformation;
      if (!shipInfo) return null;
      return `${shipInfo.address1}, ${shipInfo.city}, ${shipInfo.region} ${shipInfo.postalCode}, ${shipInfo.country}`;
    }),
    paymentFullAddress: computed(() => {
      const paymentInfo = store.order.paymentPayload.paymentInfo.contact();
      return `${paymentInfo.address1}, ${paymentInfo.city}, ${paymentInfo.state} ${paymentInfo.postalCode}, ${paymentInfo.country}`;
    }),
    hasOrderNumber: computed(() => {
      return store.orderNumber().length > 0;
    }),
    currentProductType: computed(() => {
      return store.order().purchaseInformation.productType;
    }),
    currentContactName: computed(() => {
      return store.order().contactInformation.firstName + ' ' + store.order().contactInformation.lastName
    }),
    isAcademic: computed(() => {
      return store.order().companyInformation.companyType === CompanyType.Academic;
    }),
    nationalTierWebAppCappedPricing: computed(() => {
      const products = store.products() ?? [];

      const webApp = products.find(x => x.sku === 'WEBAPP' && !!x.annualMemberPriceCap);
      const bundle = products.find(x => x.sku === 'WEBAPPB' && !!x.annualMemberPriceCap);

      return {
        webApp: webApp?.annualMemberPriceCap ?? 18500,
        bundle: bundle?.annualMemberPriceCap ?? 12950
      };
    })
  })),
  withMethods(
    (
      store,
      userStore = inject(UserStore),
      companyStore = inject(CompanyStore),
      commonStore = inject(CommonStore),
      subscriptionStore = inject(SubscriptionStore),
      purchaseService = inject(PurchaseService),
      sessionStorage = inject(SessionStorageService),
      sanitizer = inject(DomSanitizer),
      logger = inject(Logger),
    ) => ({
      getProductInfo: rxMethod<void>(
        pipe(
          filter(() => (store.products.length == 0 || store.bfdProducts.length == 0) && store.productResponseStatus() === ResponseStatus.IDLE),
          tap(() => {
            return patchState(store, { productResponseStatus: ResponseStatus.LOADING });
          }),
          switchMap(() => {
            return purchaseService.getProducts().pipe(
              tapResponse({
                next: (resp: IResponse<Product[]>) => {
                  patchState(store, {
                    products: resp.data,
                    productResponseStatus: ResponseStatus.LOADED,
                  });

                  sessionStorage.setItem(PRODUCTS_STORAGE_KEY, resp.data);
                },
                error: (err) => {
                  logger.error('unable to load products', err);
                  patchState(store, { productResponseStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
          switchMap(() => {
            return purchaseService.getProducts(true).pipe(
              tapResponse({
                next: (resp: IResponse<Product[]>) => {
                  const products = resp.data?.filter(x => x.sku === 'BFDAPP');
                  patchState(store, {
                    bfdProducts: products,
                    productResponseStatus: ResponseStatus.LOADED,
                  });

                  sessionStorage.setItem(BFD_PRODUCTS_STORAGE_KEY, products);
                },
                error: (err) => {
                  logger.error('unable to load products', err);
                  patchState(store, { productResponseStatus: ResponseStatus.ERROR });
                },
              }),
            );
          })
        ),
      ),
      verifyMembership: rxMethod<RequestMembershipInfo>(
        pipe(
          tap(() => patchState(store, {
            suggestedCompanyName: '',
            membershipResponseStatus: ResponseStatus.LOADING
          })),
          switchMap((request: RequestMembershipInfo) => {
            return purchaseService.getMembershipStatus(request).pipe(
              tapResponse({
                next: (resp: IResponse<string>) => {
                  if (resp.code == MemberIdResponseCodes.COMPANY_NAME_SUGGESTED) {
                    patchState(store, {
                      suggestedCompanyName: resp.data,
                      membershipResponseStatus: ResponseStatus.IDLE
                    });
                  } else {
                    patchState(store, {
                      membershipVerification: resp.success,
                      membershipResponseStatus: resp.success
                        ? ResponseStatus.LOADED
                        : ResponseStatus.ERROR,
                    });
                  }
                },
                error: (err) => {
                  logger.error('unable to load membership status', err);
                  patchState(store, { membershipResponseStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
        ),
      ),
      submitOrder: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { orderSubmissionStatus: ResponseStatus.LOADING })),
          switchMap(() => {
            return purchaseService.submitOrder(store.order()).pipe(
              tapResponse({
                next: (resp: IResponse<any>) => {

                  const paymentInfo = store.basicPaymentInfo();
                  paymentInfo.productName = store.selectedProduct().productName;
                  paymentInfo.paymentId = resp.data.paymentId;
                  paymentInfo.licenses = store.selectedProduct().productName.includes('Bundle')
                    ? store.order().bundleInformation.licenseSeats : store.order().purchaseInformation.licenseSeats;
                  paymentInfo.userEmail = userStore.user().email && userStore.user().email?.trim() !== ''
                    ? userStore.user().email
                    : store.order().contactInformation.email;

                  if (resp.success &&
                    [
                      PurchaseResponseCodes.Complete,
                      PurchaseResponseCodes.PaymentSavedIssue
                    ].includes(resp.code ?? -1)) {
                    patchState(store, {
                      orderNumber: resp.data.transactionId,
                      order: {
                        ...store.order(),
                        companyInformation: {
                          ...store.order().companyInformation,
                          companyId: resp.data.companyId,
                        },
                        isForExistingCompany: store.isLicenseThenBook()
                      },
                      orderSubmissionStatus: ResponseStatus.LOADED
                    });
                  }

                  if (!resp.success) {
                    patchState(store, { orderSubmissionStatus: ResponseStatus.ERROR });
                  } else if ((userStore.user.companyId()?.trim() ?? '') !== '') {
                    subscriptionStore.loadSubscriptions();
                    patchState(store, {
                      basicPaymentInfo: paymentInfo,
                      orderSubmissionStatus: ResponseStatus.LOADED
                    })
                  }
                },
                error: () => {
                  patchState(store, { orderSubmissionStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
        ),
      ),
      getProductDetails: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { productResponseStatus: ResponseStatus.LOADING })),
          switchMap((type: number) => {
            return purchaseService.getProductDetails(type).pipe(
              tapResponse({
                next: (resp: IResponse<any>) => {
                  patchState(store, {
                    nmfcBookDetail: resp.data,
                    productResponseStatus: ResponseStatus.LOADED,
                  });
                },
                error: (err) => {
                  logger.error('unable to load products', err);
                  patchState(store, { productResponseStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
        ),
      ),
      submitNMFCBookOrder: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { orderSubmissionStatus: ResponseStatus.LOADING })),
          switchMap(() => {
            return purchaseService.purchaseNMFCBook(store.order()).pipe(
              tapResponse({
                next: (resp: IResponse<any>) => {

                  const paymentInfo = store.basicPaymentInfo();
                  paymentInfo.productName = store.selectedProduct().productName;
                  paymentInfo.paymentId = resp.data.paymentId;
                  paymentInfo.quantity = store.order().purchaseInformation.quantity;
                  paymentInfo.userEmail = userStore.user().email && userStore.user().email?.trim() !== ''
                    ? userStore.user().email
                    : store.order().contactInformation.email;

                  if (resp.success &&
                    [
                      PurchaseResponseCodes.Complete,
                      PurchaseResponseCodes.PaymentSavedIssue
                    ].includes(resp.code ?? -1)) {
                    patchState(store, {
                      orderNumber: resp.data.transactionId,
                      basicPaymentInfo: paymentInfo,
                      orderSubmissionStatus: ResponseStatus.LOADED
                    });
                  }

                  if (!resp.success) {
                    patchState(store, { orderSubmissionStatus: ResponseStatus.ERROR });
                  }
                },
                error: () => {
                  patchState(store, { orderSubmissionStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
        ),
      ),
      resetMembershipVerification() {
        patchState(store, { membershipVerification: false });
      },
      resetMembershipVerificationState() {
        patchState(store, { membershipResponseStatus: ResponseStatus.IDLE });
      },
      resetStore() {
        //Clear all but the products
        const newInitState = {
          ...initialState,
          completedSteps: [false, false, false, false, false, false],
          products: store.products(),
          productResponseStatus: ResponseStatus.IDLE,
          isLicenseThenBook: false
        };

        patchState(store, newInitState);
      },
      setStepAsComplete(stepIndex: number) {
        const currentSteps = store.completedSteps();
        currentSteps[stepIndex] = true;
        patchState(store, { completedSteps: currentSteps });
      },
      setProductType(selectedProductType: ProductType, isBundle: boolean) {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (draft) => {
          draft.purchaseInformation.productType = selectedProductType;
        });

        const currentBundleState = store.bundledState();
        const updatedBundleState = produce(currentBundleState, (draft) => {
          draft.isBundled = isBundle;
          draft.product = undefined;
        });
        patchState(store, { order: updatedOrder, bundledState: updatedBundleState });
      },
      setLicenseThenBookMode(enabled: boolean) {
        patchState(store, { isLicenseThenBook: enabled });
      },
      setUserInfo(user: AvailableCompanyUser | null) {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (thisOrder) => {
          thisOrder.contactInformation.firstName = user?.firstName ?? '';
          thisOrder.contactInformation.lastName = user?.lastName ?? '';
          thisOrder.contactInformation.email = user?.email ?? '';
          thisOrder.contactInformation.jobTitle = user?.jobTitle ?? '';
          thisOrder.contactInformation.phoneNumber = user?.phoneNumber ?? '';
          thisOrder.companyInformation.userId = user?.userId ?? null;
        });

        patchState(store, { order: updatedOrder });
      },
      setUserAndCompanyInfo(companyFormData: CompanyFormData) {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (thisOrder) => {
          // update contact info
          thisOrder.contactInformation.firstName = companyFormData.firstName;
          thisOrder.contactInformation.lastName = companyFormData.lastName;
          thisOrder.contactInformation.email = companyFormData.email;
          thisOrder.contactInformation.jobTitle = companyFormData.jobTitle;
          thisOrder.contactInformation.phoneNumber = companyFormData.phoneNumber;

          // update company info
          thisOrder.companyInformation.userId = userStore.user.userId()?.length ? userStore.user.userId() : null;
          thisOrder.companyInformation.companyId =
            store.selectedCompany()?.companyId?.length
              ? store.selectedCompany()?.companyId ?? ''
              : companyStore.company.companyId()?.length
                ? companyStore.company.companyId()
                : userStore.user.companyId()?.length
                  ? userStore.user.companyId()
                  : null;
          thisOrder.companyInformation.companyType = companyFormData.companyType;
          thisOrder.companyInformation.memberId = companyFormData.memberId?.length ? companyFormData.memberId : null;
          thisOrder.companyInformation.companyName = companyFormData.companyName;
          thisOrder.companyInformation.country = companyFormData.country;
          thisOrder.companyInformation.postalCode = companyFormData.postalCode;
          thisOrder.companyInformation.city = companyFormData.city;
          thisOrder.companyInformation.region = companyFormData.region;
          thisOrder.companyInformation.address1 = companyFormData.address1;
          thisOrder.companyInformation.companyType = companyFormData.companyType;
          thisOrder.companyInformation.hasShipInformation = companyFormData.hasShipInformation;

          if (companyFormData.hasShipInformation) {
            thisOrder.shippingInformation = {
              firstName: companyFormData.shipInformationForm?.firstName ?? '',
              lastName: companyFormData.shipInformationForm?.lastName ?? '',
              address1: companyFormData.shipInformationForm?.address1 ?? '',
              postalCode: companyFormData.shipInformationForm?.postalCode ?? '',
              city: companyFormData.shipInformationForm?.city ?? '',
              region: companyFormData.shipInformationForm?.region ?? '',
              country: companyFormData.shipInformationForm?.country ?? ''
            }


          }
        });

        patchState(store, { order: updatedOrder });
      },
      setTotalAmount(totalAmount: string) {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (thisOrder) => {
          thisOrder.paymentPayload.paymentInfo.totalAmount = totalAmount;
        });

        patchState(store, { order: updatedOrder });
      },
      setLicenseInfo(licenseSeats: number, pricePerLicense: number, totalAmount: string, selectedProduct: Product, additionalLicensesPurchased = false) {
        const currentOrder = store.order();

        if (store.bundledState().isBundled) {
          const bundledOrder = store.bundledState();
          const updatedBundledOrder = produce(bundledOrder, (thisOrder) => {
            thisOrder.product = {
              product: selectedProduct,
              licenses: licenseSeats,
              totalAmount: totalAmount
            };
          });

          const updatedOrder = produce(currentOrder, (thisOrder) => {
            thisOrder.bundleInformation = {
              subscriptionTierId: selectedProduct.productPricingTierId,
              licenseSeats: licenseSeats
            };
          });

          patchState(store, { order: updatedOrder, bundledState: updatedBundledOrder, additionalLicensesPurchased: additionalLicensesPurchased });
        } else {
          const updatedOrder = produce(currentOrder, (thisOrder) => {
            thisOrder.purchaseInformation.licenseSeats = licenseSeats;
            thisOrder.purchaseInformation.subscriptionTierId = selectedProduct.productPricingTierId;
            thisOrder.paymentPayload.paymentInfo.totalAmount = totalAmount;
          });

          patchState(store, { selectedProduct, pricePerLicense, order: updatedOrder, additionalLicensesPurchased: additionalLicensesPurchased });
        }
      },
      setApiTierInfo(selectedSubscriptionTier: string, selectedProduct: Product, totalCost: number) {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (thisOrder) => {
          thisOrder.purchaseInformation.subscriptionTierId = selectedProduct.productPricingTierId;
          //Should set the selectedSubscriptionTier separated somewhere?
          thisOrder.paymentPayload.paymentInfo.totalAmount = totalCost.toString();
        });

        patchState(store, { selectedProduct, order: updatedOrder });
      },
      setNMFCBookInfo(totalQuantity: number, totalPrice: string, selectedProduct: Product): void {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (draft) => {
          draft.purchaseInformation.quantity = totalQuantity;
          draft.purchaseInformation.subscriptionTierId = selectedProduct.productPricingTierId;
          draft.paymentPayload.paymentInfo.totalAmount = totalPrice;
        });
        patchState(store, { selectedProduct, order: updatedOrder });
      },
      setPaymentPayload(paymentPayload: PaymentPayload) {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (thisOrder) => {
          // Since the payment payload is a flat object, we need to map it to the nested structure
          thisOrder.paymentPayload.paymentInfo.paymentType = paymentPayload.paymentMethod;
          thisOrder.paymentPayload.paymentInfo.totalAmount = paymentPayload.totalAmount;
          thisOrder.paymentPayload.paymentInfo.currency = paymentPayload.currency;
          thisOrder.paymentPayload.paymentInfo.contact.firstName = paymentPayload.firstName;
          thisOrder.paymentPayload.paymentInfo.contact.lastName = paymentPayload.lastName;
          thisOrder.paymentPayload.paymentInfo.contact.address1 = paymentPayload.address1;
          thisOrder.paymentPayload.paymentInfo.contact.city = paymentPayload.city;
          thisOrder.paymentPayload.paymentInfo.contact.state = paymentPayload.state;
          thisOrder.paymentPayload.paymentInfo.contact.postalCode = paymentPayload.postalCode;
          thisOrder.paymentPayload.paymentInfo.contact.country = paymentPayload.country;
          thisOrder.paymentPayload.paymentInfo.contact.email = paymentPayload.email;
          thisOrder.paymentPayload.paymentInfo.contact.phone = paymentPayload.phone;
          thisOrder.paymentPayload.paymentInfo.creditCard.cardNumber = paymentPayload.cardNumber;
          thisOrder.paymentPayload.paymentInfo.creditCard.expirationMonth = paymentPayload.cardExpirationMonth;
          thisOrder.paymentPayload.paymentInfo.creditCard.expirationYear = paymentPayload.cardExpirationYear.toString();
          thisOrder.paymentPayload.paymentInfo.creditCard.cvv = paymentPayload.cardCVV;
          thisOrder.paymentPayload.paymentInfo.bankAccount.accountType = paymentPayload.bankAccountType;
          thisOrder.paymentPayload.paymentInfo.bankAccount.accountOwner = paymentPayload.bankAccountOwner;
          thisOrder.paymentPayload.paymentInfo.bankAccount.accountNumber = paymentPayload.bankAccountNumber;
          thisOrder.paymentPayload.paymentInfo.bankAccount.routingNumber = paymentPayload.bankRoutingNumber;
          thisOrder.paymentPayload.paymentInfo.check.checkNumber = paymentPayload.checkNumber;
          thisOrder.paymentPayload.paymentInfo.check.wireIdentifier = paymentPayload.wireIdentifier;
          thisOrder.paymentPayload.paymentInfo.check.originatingPartyName = paymentPayload.originatingPartyName;
          thisOrder.paymentPayload.companyId =
            store.selectedCompany()?.companyId?.length
              ? store.selectedCompany()?.companyId ?? ''
              : companyStore.company.companyId()?.length
                ? companyStore.company.companyId()
                : userStore.user.companyId()?.length
                  ? userStore.user.companyId()
                  : null;
          thisOrder.paymentPayload.userId = userStore.user.userId()?.length ? userStore.user.userId() : null;
          thisOrder.paymentPayload.glCode = store.selectedProduct.glAccount();
          thisOrder.paymentPayload.productName = store.selectedProduct.productName();
        });

        patchState(store, { order: updatedOrder });

        this.setACHDiscount(updatedOrder.paymentPayload);
      },
      setBundledProduct(product: BundledProduct) {
        const currentBundledState = store.bundledState();
        const updatedBundledState = produce(currentBundledState, (bundledState) => {
          bundledState.product = product;
        });

        patchState(store, { bundledState: updatedBundledState });
      },
      calculateTotalBundleAmount() {
        let totalAmount = 0;
        const currentBundledState = store.bundledState();
        const currentProduct = store.selectedProduct(); // Should be an API product
        const isMember = store.membershipVerification();
        const order = store.order();
        const currentPaymentPayload = store.order.paymentPayload();

        if (currentBundledState && currentBundledState.isBundled) {
          const currentAmount = parseFloat(currentBundledState.product?.totalAmount ?? '0');

          if (!isNaN(currentAmount)) {
            totalAmount += currentAmount;
          }
        }

        if (isMember)
          totalAmount += currentProduct.memberCost;
        else
          totalAmount += currentProduct.nonMemberCost;

        // 30% off
        // totalAmount *= 0.7; // CLASSIT-1592 - Remove bundle math, price is in db

        const updatedPaymentPayload = produce(currentPaymentPayload, (payload) => {
          payload.paymentInfo.totalAmount = totalAmount.toString()
        });

        patchState(store, { order: { ...order, paymentPayload: updatedPaymentPayload } });
      },
      setACHDiscount(paymentPayload: OrderPayment) {
        if (paymentPayload.paymentInfo.paymentType === 1) {
          const currentOrder = store.order();

          const totalAmount = parseFloat(paymentPayload.paymentInfo.totalAmount) || 0;
          const rawDiscountRate = commonStore.variables().achDiscountPricing ?? 0;

          // Support both discount shapes: decimal rates (0.025) and percentage values (2.5).
          const discountRate = rawDiscountRate > 1 ? rawDiscountRate / 100 : rawDiscountRate;
          const totalAmountInCents = Math.round((totalAmount + Number.EPSILON) * 100);
          const discountedTotalInCents = Math.round((totalAmountInCents * (1 - discountRate)) + Number.EPSILON);
          const discountAmount = (discountedTotalInCents / 100).toFixed(2);

          const updatedOrder = produce(currentOrder, (thisOrder) => {
            thisOrder.paymentPayload.paymentInfo.discountAmount = discountAmount;
          });

          patchState(store, { order: updatedOrder });
        }
      },
      setOrderNumber(number: string): void {
        patchState(store, { orderNumber: number, orderSubmissionStatus: ResponseStatus.LOADED });
      },
      resetOrderNumber(): void {
        patchState(store, { orderNumber: '' });
      },
      getDocumentSigningUrl(): SafeResourceUrl {
        const documentParams = [
          { field: 'Company Name', value: store.order().companyInformation.companyName },
          { field: 'Company Address', value: store.companyFullAddress() },
          { field: 'Licensee Name', value: store.currentContactName() },
          { field: 'Licensee Job Title', value: store.order().contactInformation.jobTitle }
        ];

        const fragment = documentParams
          .map(p => `${encodeURIComponent(p.field)}=${encodeURIComponent(p.value)}`)
          .join('&');

        let url = '';

        switch (store.currentProductType()) {
          case ProductType.API:
            url = `${store.adobeSigningUrl()}#${fragment}&hosted=false`;
            break;
          case ProductType.BFD:
            url = '';
            break;
        }

        return sanitizer.bypassSecurityTrustResourceUrl(url);
      },
      setAgreementId(agreementId: string): void {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (draft) => {
          draft.companyInformation.agreementId = agreementId;
        });

        patchState(store, { order: updatedOrder });
      },
      setWebsiteSeatsNationallyCapped(isCapped: boolean): void {
        patchState(store, { websiteSeatsNationallyCapped: isCapped });
      },
      setBasicPaymentInfo(paymentInfo: BasicPaymentInfo): void {
        patchState(store, {
          basicPaymentInfo: {
            productName: paymentInfo.productName,
            paymentId: paymentInfo.paymentId,
            licenses: paymentInfo.licenses,
            forAdditionalLicenses: paymentInfo.forAdditionalLicenses ?? false,
          }, orderSubmissionStatus: ResponseStatus.LOADED
        });
      },
      setRenewalMode(isRenewing: boolean): void {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (draft) => {
          draft.purchaseMode = isRenewing ? PurchaseMode.RENEWAL : PurchaseMode.PURCHASE;
        });

        patchState(store, { order: updatedOrder });
      },
      setSelectedCompany(company: Company): void {
        patchState(store, { selectedCompany: company });
      },
      setMembershipVerificationState(status: ResponseStatus, setVerified: boolean) {
        patchState(store, { membershipResponseStatus: status });

        if (setVerified)
          patchState(store, { membershipVerification: true });
      },
      setCompanyAssociation(isAssociated: boolean): void {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (draft) => {
          draft.csWithAssociatedCompanyPurchase = isAssociated;
        });

        patchState(store, { order: updatedOrder });
      },
      setPaymentInfoEntered(isEntered: boolean): void {
        patchState(store, { paymentInfoEntered: isEntered });
      },
      setIsForExistingCompany(isForExistingCompany: boolean): void {
        const currentOrder = store.order();
        const updatedOrder = produce(currentOrder, (draft) => {
          draft.isForExistingCompany = isForExistingCompany;
        });

        patchState(store, { order: updatedOrder });
      },
      getAdobeSigningUrl: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { adobeSigningUrl: null })),
          switchMap(() => {
            return purchaseService.getAdobeSigningUrl().pipe(
              tapResponse({
                next: (resp: IResponse<string>) => {
                  if (resp.success) {
                    patchState(store, { adobeSigningUrl: resp.data });
                  }
                },
                error: () => {
                  patchState(store, { adobeSigningUrl: null });
                },
              })
            );
          }),
        ),
      )
    }),
  ),
  withHooks({
    onInit: (
      store,
      userStore = inject(UserStore),
      companyStore = inject(CompanyStore),
      subscriptionStore = inject(SubscriptionStore)
    ) => {
      effect(() => {
        if (store.purchaseInitializationState() === PurchaseInitializationState.INITIALIZED)
          return;

        if ((userStore.user()?.email?.length ?? 0) > 0 && !userStore.isCustomerService()) {
          if (store.purchaseInitializationState() === PurchaseInitializationState.LOADING_COMPANY) {
            if (companyStore.submissionStatus() !== ResponseStatus.LOADED) {
              companyStore.loadCompanyInfo(userStore.user().companyId);
            } else {
              patchState(store, {
                purchaseInitializationState: PurchaseInitializationState.LOADING_SUBSCRIPTIONS
              });
            }
          } else if (store.purchaseInitializationState() === PurchaseInitializationState.LOADING_SUBSCRIPTIONS) {
            if (subscriptionStore.submissionStatus() !== ResponseStatus.LOADED) {
              subscriptionStore.loadSubscriptions();
            } else {
              patchState(store, {
                purchaseInitializationState: PurchaseInitializationState.LOADING_PRODUCTS
              });
            }
          } else {
            if (store.productResponseStatus() !== ResponseStatus.LOADED) {
              store.getProductInfo();
            } else {
              patchState(store, {
                purchaseInitializationState: PurchaseInitializationState.INITIALIZED
              });
            }
          }
        } else {
          // We're either not logged in or logged in as customer service (purchase on behalf of).
          // Therefore no subs to check, go straight to loading products.
          store.getProductInfo();

          patchState(store, {
            purchaseInitializationState: PurchaseInitializationState.INITIALIZED
          });
        }
      });
    }
  })
);
